import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  projectStorage,
  projectFirestore,
  timestamp,
} from "../firebase-config/config";
import { useAuthContext } from "../firebase-files/hooks/useAuthContext";
import { useDocument } from "../firebase-files/hooks/useDocument";
import "./FooterUpper.css";

const FooterUpper = () => {
  const [showModal, setShowModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const fileInputRef = useRef(null);
  const { user } = useAuthContext();

  // Contact details from Firestore
  const { document: contactDetails } = useDocument(
    "settings",
    "contactUs"
  );
  const { document: appTypeDocument } = useDocument("settings", "AppType");
  const { document: footerBgImage } = useDocument("media", "footer_upper_image");

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Preview image when user selects file
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
      setError(null);
    } else {
      setPreviewImage(null);
    }
  };

  // Upload image to Firebase Storage and update Firestore document
  const handleSubmit = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current.files[0];

    if (!file) {
      setError("Please select an image first");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Generate unique filename for storage
      const fileName = `footer_${Date.now()}_${file.name}`;
      const storageRef = projectStorage
        .ref()
        .child(`footer_upper_image/${fileName}`);

      // Upload to storage
      const snapshot = await storageRef.put(file);
      const downloadURL = await snapshot.ref.getDownloadURL();

      // Update the document in Firestore
      await projectFirestore
        .collection("media")
        .doc("footer_upper_image")
        .set(
          {
            url: downloadURL,
            type: "footer",
            category: "footer upper",
            mediaType: "IMAGE",
            createdAt: timestamp.fromDate(new Date()),
            updatedAt: timestamp.fromDate(new Date()),
          },
          { merge: true }
        );

      setShowModal(false);
      setPreviewImage(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload image: " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="footer-upper-container">
      <section className="footer_before relative">
        {/* Background Image with img tag */}
        {footerBgImage && footerBgImage.url && (
          <img 
            src={footerBgImage.url} 
            alt="Footer Background" 
            className="footer-background-image"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: -1
            }}
          />
        )}
        
        {/* Overlay */}
        <div className="footer-overlay"></div>

        {/* Camera Icon - Only visible to admin */}
        {user && user.role === "admin" && (
          <div
            className="footer-camera-icon"
            onClick={() => setShowModal(true)}
            title="Change footer background"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24px"
              viewBox="0 -960 960 960"
              width="24px"
              fill="black"
            >
              <path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h160l80-80h160l80 80h160q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm0-80h640v-480H640l-80-80H400l-80 80H160v480Zm320-240Z" />
            </svg>
          </div>
        )}

        <div className="container">
          <div className="row footer_upper_row">
            <div className="col-4 col-md-2 mt-2">
              <a
                href={
                  "mailto:" + (contactDetails && contactDetails.contactEmail)
                }
                style={{ color: "black" }}
              >
                <div className="d-flex flex-column footer_upper_boxes">
                  <div className="footer_uuper_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="28px"
                      viewBox="0 -960 960 960"
                      width="28px"
                      fill="currentColor"
                    >
                      <path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z" />
                    </svg>{" "}
                  </div>
                  <div>Email</div>
                </div>
              </a>
            </div>
            <div className="col-4 col-md-2 mt-2">
              <a
                href={"tel:" + (contactDetails && contactDetails.contactNumber)}
                style={{ color: "black" }}
              >
                <div className="d-flex flex-column footer_upper_boxes">
                  <div className="footer_uuper_icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="24px"
                      viewBox="0 -960 960 960"
                      width="24px"
                      fill="currentColor"
                    >
                      <path d="M480-440v-360q0-17 11.5-28.5T520-840h280q17 0 28.5 11.5T840-800v200q0 17-11.5 28.5T800-560H600L480-440Zm80-200h200v-120H560v120Zm0 0v-120 120Zm238 520q-125 0-247-54.5T329-329Q229-429 174.5-551T120-798q0-18 12-30t30-12h162q14 0 25 9.5t13 22.5l26 140q2 16-1 27t-11 19l-97 98q20 37 47.5 71.5T387-386q31 31 65 57.5t72 48.5l94-94q9-9 23.5-13.5T670-390l138 28q14 4 23 14.5t9 23.5v162q0 18-12 30t-30 12ZM241-600l66-66-17-94h-89q5 41 14 81t26 79Zm358 358q39 17 79.5 27t81.5 13v-88l-94-19-67 67ZM241-600Zm358 358Z" />
                    </svg>
                  </div>
                  <div>Phone</div>
                </div>
              </a>
            </div>

            {(appTypeDocument && appTypeDocument.apptype === "liteplus") ||
            (appTypeDocument && appTypeDocument.apptype === "pro") ? (
              <div className="col-4 col-md-2 mt-2">
                <Link to="/notifications">
                  <div className="d-flex flex-column footer_upper_boxes">
                    <div className="footer_uuper_icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="28px"
                        viewBox="0 -960 960 960"
                        width="28px"
                        fill="currentColor"
                      >
                        <path d="M720-440v-80h160v80H720Zm48 280-128-96 48-64 128 96-48 64Zm-80-480-48-64 128-96 48 64-128 96ZM200-200v-160h-40q-33 0-56.5-23.5T80-440v-80q0-33 23.5-56.5T160-600h160l200-120v480L320-360h-40v160h-80Zm240-182v-196l-98 58H160v80h182l98 58Zm120 36v-268q27 24 43.5 58.5T620-480q0 41-16.5 75.5T560-346ZM300-480Z" />
                      </svg>{" "}
                    </div>
                    <div>Notification</div>
                  </div>
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* Modal remains the same */}
      {showModal && (
        <div
          className="footer-modal-overlay"
          onClick={() => {
            setShowModal(false);
            setPreviewImage(null);
            setError(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        >
          <div
            className="footer-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Upload Background Image</h3>
            <p>Recommended size: 1200x400 pixels </p>

            <form onSubmit={handleSubmit}>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageChange}
                className="footer-file-input"
                required
              />

              {previewImage && (
                <div className="footer-image-preview">
                  <img src={previewImage} alt="Preview" />
                </div>
              )}

              {error && <div className="footer-error-message">{error}</div>}

              <div className="footer-modal-actions">
                <button
                  type="button"
                  className="footer-cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setPreviewImage(null);
                    setError(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="footer-save-btn"
                  disabled={!previewImage || isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="footer-spinner"></span>
                      Uploading...
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FooterUpper;